/**
 * i18n: vue-i18n (runtime-only build, own tiny message compiler – no eval, CSP safe) with
 *  - bundled UI texts of the web client (messages/de, en, fr, nl; missing keys fall back to en),
 *  - the legacy dictionaries ILANG / LANG served by GET /api/v2/i18n/{locale}?scopes=…,
 *    available as `t('ILANG.0001')` and through `tx(text)`, which substitutes %LANG_x% /
 *    %ILANG_x% placeholders in texts from meta with the same code as the server
 *    (`@creator/core/i18n`).
 * Switching the locale reloads the dictionaries of all requested scopes (ETag-revalidated).
 */
import { substitute, type Dictionaries } from '@creator/core/i18n';
import { shallowRef } from 'vue';
import { createI18n, type MessageCompiler, type MessageContext } from 'vue-i18n';
import { api, type ApiClient } from '../api/client';
import type { I18nBundle } from '../api/types';
import de from './messages/de';
import en from './messages/en';
import fr from './messages/fr';
import nl from './messages/nl';
import { tableTexts } from './table';

export const LOCALES = ['de', 'en', 'fr', 'nl'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'de';
export const BASE_SCOPES = ['internal', 'global'] as const;

const ui: Record<Locale, Record<string, unknown>> = { de, en, fr, nl };

export const isLocale = (v: unknown): v is Locale => typeof v === 'string' && (LOCALES as readonly string[]).includes(v);

/** Preference (if valid) → browser languages → default. */
export function resolveLocale(pref: string | undefined | null, languages: readonly string[] = []): Locale {
  if (isLocale(pref)) return pref;
  for (const l of languages) {
    const short = l.slice(0, 2).toLowerCase();
    if (isLocale(short)) return short;
  }
  return DEFAULT_LOCALE;
}

/**
 * Message compiler: `{name}` / `{0}` interpolation only. Legacy texts may contain characters the
 * vue-i18n syntax reserves (`@`, `|`, `{`), so they must not go through the full compiler; and the
 * runtime-only build avoids `new Function` entirely.
 */
export const compileMessage: MessageCompiler = (message) => {
  const src = typeof message === 'string' ? message : '';
  const fn = (ctx: MessageContext) =>
    src.includes('{')
      ? src.replace(/\{\s*([A-Za-z0-9_]+)\s*\}/g, (m, k: string) => {
          const v = /^\d+$/.test(k) ? ctx.list(Number(k)) : ctx.named(k);
          return v === undefined || v === null ? m : String(v);
        })
      : src;
  return fn as never;
};

export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: 'en',
  messages: { de, en, fr, nl } as never,
  messageCompiler: compileMessage,
  missingWarn: false,
  fallbackWarn: false,
  warnHtmlMessage: false,
});

/** The global composer with plain signatures (vue-i18n's message-schema typing is too deep for tsc here). */
interface PlainComposer {
  t(key: string, params: Record<string, unknown>): string;
  locale: { value: string };
  availableLocales: string[];
  setLocaleMessage(locale: string, messages: Record<string, unknown>): void;
  mergeLocaleMessage(locale: string, messages: Record<string, unknown>): void;
}
export const composer = i18n.global as unknown as PlainComposer;

/** Translate a UI key outside templates. */
export function t(key: string, params?: Record<string, unknown>): string {
  return composer.t(key, params ?? {});
}

/** Legacy dictionaries of the current locale (reactive; read by `tx`). */
export const dictionaries = shallowRef<Dictionaries>({ LANG: {}, ILANG: {} });

/** Substitute %LANG_x% / %ILANG_x% placeholders (labels and texts from meta). */
export function tx(text: string | null | undefined): string {
  if (!text) return '';
  return substitute(text, dictionaries.value);
}

/** Legacy internal text by key, with fallback. */
export function ilang(key: string, fallback = ''): string {
  return dictionaries.value.ILANG[key] ?? fallback;
}

/** Fetches dictionaries per locale + scope set, revalidating with If-None-Match. */
export class DictionaryLoader {
  private scopes = new Set<string>(BASE_SCOPES);
  private cache = new Map<string, { etag: string | undefined; bundle: I18nBundle }>();
  constructor(private client: Pick<ApiClient, 'getWithEtag'> = api) {}

  get scopeList(): string[] {
    return [...this.scopes];
  }

  /** Add scopes (e.g. `databases/<db>`); returns true if any was new. */
  addScopes(...scopes: string[]): boolean {
    const before = this.scopes.size;
    for (const s of scopes) this.scopes.add(s);
    return this.scopes.size !== before;
  }

  /** Forget non-base scopes (logout). */
  reset(): void {
    this.scopes = new Set(BASE_SCOPES);
    this.cache.clear();
  }

  async load(locale: string): Promise<I18nBundle> {
    const scopes = this.scopeList.join(',');
    const key = `${locale}|${scopes}`;
    const cached = this.cache.get(key);
    const res = await this.client.getWithEtag<I18nBundle>(`/i18n/${encodeURIComponent(locale)}`, {
      query: { scopes },
      ...(cached?.etag ? { ifNoneMatch: cached.etag } : {}),
      allowUnauthorized: true,
    });
    if (res.notModified && cached) return cached.bundle;
    this.cache.set(key, { etag: res.etag, bundle: res.data });
    return res.data;
  }
}

export const dictionaryLoader = new DictionaryLoader();

let current: Locale = DEFAULT_LOCALE;
let pending: Promise<void> | undefined;

export const currentLocale = (): Locale => current;

/** Apply a bundle: legacy texts into vue-i18n (`ILANG.x`, `LANG.x`) and `dictionaries`. */
function apply(locale: Locale, bundle: I18nBundle): void {
  dictionaries.value = { LANG: bundle.LANG, ILANG: bundle.ILANG };
  composer.setLocaleMessage(locale, { ...ui[locale], ILANG: bundle.ILANG, LANG: bundle.LANG });
}

/** Switch the locale (loads the dictionaries of all requested scopes). */
export async function setLocale(locale: Locale): Promise<void> {
  current = locale;
  const run = (async () => {
    try {
      const bundle = await dictionaryLoader.load(locale);
      if (current !== locale) return; // superseded by a later switch
      apply(locale, bundle);
    } catch (e) {
      // UI texts are bundled; without legacy dictionaries placeholders are just removed
      console.warn('i18n: dictionaries not loaded', e);
      if (!composer.availableLocales.includes(locale)) composer.setLocaleMessage(locale, ui[locale]);
    }
    if (current !== locale) return;
    composer.locale.value = locale;
    document.documentElement.setAttribute('lang', locale);
  })();
  pending = run;
  return run;
}

/** Request additional scopes (database / table texts); reloads if anything is new. */
export async function requestScopes(...scopes: string[]): Promise<void> {
  if (pending) await pending.catch(() => {});
  if (dictionaryLoader.addScopes(...scopes)) await setLocale(current);
}

// ---------------------------------------------------------------------------------------------
// additional bundled texts of feature modules (kept in own files: table view)
// ---------------------------------------------------------------------------------------------

function mergeTree(a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const cur = out[k];
    out[k] = v && typeof v === 'object' && cur && typeof cur === 'object' ? mergeTree(cur as Record<string, unknown>, v as Record<string, unknown>) : v;
  }
  return out;
}

/** Merge feature texts into the bundled UI texts (survives locale switches and dictionary reloads). */
export function registerMessages(texts: Partial<Record<Locale, Record<string, unknown>>>): void {
  for (const l of LOCALES) {
    const m = texts[l];
    if (!m) continue;
    ui[l] = mergeTree(ui[l], m);
    composer.mergeLocaleMessage(l, m);
  }
}
registerMessages(tableTexts);
